import { useEffect, useId, useState } from 'react';
import { call } from '../api';
import { navigate } from '../router';

/** Trophies: types, de medaille (eigen ontwerp, geen logo's) en meldingen bij nieuwe trophies. */

export type Tier = 'bronze' | 'silver' | 'gold' | 'platinum';
export interface Trophy { id: string; cat: string; tier: Tier; name: string; text: string; secret: boolean; got: number | null; progress: number | null; goal: number | null; rarity: number | null }
export interface Cabinet {
  id?: string;
  name: string;
  thumb: string;
  computedAt: number | null;
  points: number;
  level: number;
  levelPct: number;
  counts: Record<Tier, number>;
  total: number;
  earned: number;
  showcase: Trophy[];
  recent: Trophy[];
  categories: { id: string; name: string; icon: string }[];
  trophies: Trophy[];
}

export const TIER_LABEL: Record<Tier, string> = { bronze: 'Brons', silver: 'Zilver', gold: 'Goud', platinum: 'Platina' };

const COLORS: Record<Tier, [string, string, string]> = {
  bronze: ['#e3a06a', '#9a5a2c', '#5c3418'],
  silver: ['#f4f6f8', '#aab1ba', '#5f6670'],
  gold: ['#ffe48a', '#d9a520', '#7d5a06'],
  platinum: ['#effaff', '#9fd3ef', '#3f7fa6'],
};

const CAT_ICON: Record<string, string> = {
  time: 'clock', movies: 'film', series: 'tv', marathon: 'flame', rhythm: 'calendar',
  explore: 'compass', sport: 'ball', music: 'note', social: 'people', secret: 'star',
};

function Glyph({ icon }: { icon: string }) {
  switch (icon) {
    case 'clock': return <><circle cx="12" cy="12" r="8" /><path d="M12 7.5V12l3 2" /></>;
    case 'film': return <><rect x="4" y="5" width="16" height="14" rx="2" /><path d="M8 5v14M16 5v14M4 9.5h4M4 14.5h4M16 9.5h4M16 14.5h4" /></>;
    case 'tv': return <><rect x="3" y="6.5" width="18" height="12" rx="2" /><path d="M8.5 21h7M9 3.5l3 3 3-3" /></>;
    case 'flame': return <path d="M12 3c1 3.5 5 5.2 5 10a5 5 0 0 1-10 0c0-2.6 1.6-3.8 2-5.8 1 .9 1.7 1.9 2.6 2.2.2-2.2-.6-4.2.4-6.4z" />;
    case 'calendar': return <><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M4 10h16M9 3v4M15 3v4" /><path d="M9 14.5l2 2 4-4" /></>;
    case 'compass': return <><circle cx="12" cy="12" r="8" /><path d="M15.5 8.5l-2 5-5 2 2-5z" /></>;
    case 'ball': return <><circle cx="12" cy="12" r="8" /><path d="M12 8.2l3.2 2.3-1.2 3.8h-4l-1.2-3.8zM12 4v4.2M15.2 10.5l4-1.3M14 14.3l2.4 3.5M10 14.3l-2.4 3.5M8.8 10.5l-4-1.3" /></>;
    case 'note': return <><path d="M9 18V6.5l10-2.5v12" /><circle cx="7" cy="18" r="2" /><circle cx="17" cy="16" r="2" /></>;
    case 'people': return <><circle cx="9" cy="9" r="3" /><circle cx="17" cy="10" r="2.4" /><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5M15.5 14.5c2.8 0 5 1.6 5 4.5" /></>;
    default: return <path d="M12 4l2.4 5 5.4.6-4 3.8 1.1 5.4L12 16.2 7.1 18.8l1.1-5.4-4-3.8 5.4-.6z" />;
  }
}

/** Een medaille: rand en schijf in de kleur van het niveau, het icoon van de categorie in het midden. Grijs zolang je hem niet hebt. */
export function Medal({ t, size = 72 }: { t: Pick<Trophy, 'tier' | 'cat' | 'got' | 'secret'>; size?: number }) {
  const id = useId().replace(/:/g, '');
  const locked = !t.got;
  const [hi, mid, lo] = locked ? ['#5a5f66', '#3a3e44', '#26292d'] : COLORS[t.tier];
  const hidden = locked && t.secret;
  const ribbon = t.tier === 'gold' || t.tier === 'platinum';
  return (
    <svg className={`medal is-${t.tier}${locked ? ' is-locked' : ''}`} width={size} height={size * 1.12} viewBox="0 0 64 72" aria-hidden="true">
      <defs>
        <linearGradient id={`r${id}`} x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={hi} /><stop offset=".55" stopColor={mid} /><stop offset="1" stopColor={lo} /></linearGradient>
        <radialGradient id={`d${id}`} cx=".35" cy=".3" r=".9"><stop offset="0" stopColor={mid} /><stop offset="1" stopColor={lo} /></radialGradient>
      </defs>
      {ribbon && (
        <g opacity={locked ? 0.5 : 1}>
          <path d="M20 50l-6 20 9-4 5 6 4-18z" fill={lo} />
          <path d="M44 50l6 20-9-4-5 6-4-18z" fill={lo} />
        </g>
      )}
      {t.tier === 'platinum' ? (
        <path d="M32 2l26 15v30L32 62 6 47V17z" fill={`url(#r${id})`} />
      ) : (
        <circle cx="32" cy="32" r="30" fill={`url(#r${id})`} />
      )}
      {t.tier === 'platinum' ? (
        <path d="M32 9l20 11.5v23L32 55 12 43.5v-23z" fill={`url(#d${id})`} />
      ) : (
        <circle cx="32" cy="32" r="23.5" fill={`url(#d${id})`} />
      )}
      <circle cx="32" cy="32" r="23.5" fill="none" stroke={hi} strokeOpacity=".35" strokeDasharray="1.5 3" display={t.tier === 'platinum' ? 'none' : undefined} />
      <g transform="translate(20 20)" fill="none" stroke={locked ? '#8a9099' : hi} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        {hidden ? <text x="12" y="17" textAnchor="middle" fontSize="15" fontWeight="800" fill="#8a9099" stroke="none">?</text> : <Glyph icon={CAT_ICON[t.cat] ?? 'star'} />}
      </g>
      {!locked && <ellipse cx="24" cy="14" rx="12" ry="4" fill="#fff" opacity=".18" transform="rotate(-25 24 14)" />}
    </svg>
  );
}

/** Ring met het level in het midden. */
export function LevelRing({ level, pct, size = 88 }: { level: number; pct: number; size?: number }) {
  const r = 40;
  const c = 2 * Math.PI * r;
  return (
    <svg className="level-ring" width={size} height={size} viewBox="0 0 96 96" role="img" aria-label={`Level ${level}, ${pct}% naar het volgende level`}>
      <circle cx="48" cy="48" r={r} fill="none" stroke="var(--c-border)" strokeWidth="7" />
      <circle cx="48" cy="48" r={r} fill="none" stroke="var(--c-accent)" strokeWidth="7" strokeLinecap="round" strokeDasharray={`${(pct / 100) * c} ${c}`} transform="rotate(-90 48 48)" />
      <text x="48" y="44" textAnchor="middle" fontSize="11" fill="currentColor" opacity=".7">LEVEL</text>
      <text x="48" y="66" textAnchor="middle" fontSize="26" fontWeight="800" fill="currentColor">{level}</text>
    </svg>
  );
}

/** Meldt nieuwe trophies (ook die je verdiende terwijl de app dicht was). */
export function TrophyWatcher() {
  const [list, setList] = useState<Trophy[]>([]);

  useEffect(() => {
    let alive = true;
    const check = () => {
      call<{ trophies: Trophy[] }>('/api/trophies/unseen')
        .then((r) => {
          if (!alive || !r.trophies.length) return;
          setList((cur) => [...cur, ...r.trophies.filter((t) => !cur.some((c) => c.id === t.id))]);
          void call('/api/trophies/seen', { method: 'POST' }).catch(() => undefined);
        })
        .catch(() => undefined);
    };
    const first = window.setTimeout(check, 8000);
    const t = window.setInterval(check, 15 * 60_000);
    return () => { alive = false; window.clearTimeout(first); window.clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!list.length) return;
    const t = window.setTimeout(() => setList((l) => l.slice(1)), 9000);
    return () => window.clearTimeout(t);
  }, [list]);

  const t = list[0];
  if (!t) return null;
  return (
    <div className="toasts toasts-left" role="region" aria-label="Trophies" aria-live="polite">
      <button className="toast trophy-toast" onClick={() => { setList([]); navigate('/profile'); }}>
        <Medal t={t} size={48} />
        <span className="toast-body">
          <span className="subtle">Trophy ontgrendeld · {TIER_LABEL[t.tier]}</span>
          <strong>{t.name}</strong>
          <span className="muted">{t.text}</span>
        </span>
      </button>
    </div>
  );
}
